import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useViewableEventTypes } from "../../providers/ViewEventTypeProvider";
import './Alerts.scss';
import AlertsModal from '../Modal/AlertsModal';
import { OtsAircraft } from '../../Models/OtsAircraft';
import { FilterOpenEvents } from '../Sort/SortOtsCards';

interface AlertsProps {
    alertModalActive: any;
    alertNotificationCLicked: (event: any) => void;
}

const Alerts: React.FC<AlertsProps> = ({alertModalActive, alertNotificationCLicked}) => {
    const openEventsMasterList = useSelector((state) => state?.openEvents?.openEventsMasterList);
    const eventTypeFilters = useSelector((state) => state?.filterSort?.filters);
    const viewEventTypes = useViewableEventTypes()?.viewEventTypes;
    const [alertCount, setAlertCount] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [liveEventsOverdueList, setLiveEventsOverdueList] = useState<any[]>([]);
    const [liveEventsFiveMinsList, setLiveEventsFiveMinsList] = useState<any[]>([]);
    const alertsModalRef = useRef<HTMLDivElement>(null);

    const openModal = () => {
        if(!isModalOpen){
            alertModalActive();
        }
        setIsModalOpen(!isModalOpen);
    };
    
    const closeModal = () => {
    setIsModalOpen(false);
    }

  useEffect(() => {
    function handleClickOutside(event) {
        if (alertsModalRef.current && !alertsModalRef.current.contains(event.target)) {
            closeModal();
        }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeModal]);


    useEffect(() => {
        const processViewableEvents = () => {
            if(Array.isArray(openEventsMasterList) && Array.isArray(viewEventTypes) && viewEventTypes.length > 0) {
                // Filtering with seleted event type filters
                let filteredEvents = FilterOpenEvents(eventTypeFilters, openEventsMasterList);
                // Filtering with viewable event types
                let viewableEvents = filteredEvents.filter((aircraft: any) => viewEventTypes.some((viewableEventType: any) => viewableEventType.Name === aircraft.EventType));
                let { overdueList, fiveMinsList } = processEvents(viewableEvents);
    
                setAlertCount(overdueList.length + fiveMinsList.length);
                setLiveEventsOverdueList(overdueList);
                setLiveEventsFiveMinsList(fiveMinsList);
            }
        };

        processViewableEvents();
        const intervalId = setInterval(processViewableEvents, 1000);
        // Clear interval on component unmount
        return () => clearInterval(intervalId);
    }, [openEventsMasterList, viewEventTypes, eventTypeFilters]);

    const processEvents = (otsevents: any[]) => {
        let overdueList: any[] = [];
        let fiveMinsList: any[] = [];

        let currentTime = new Date();
        let fiveMinsFromNow = new Date(currentTime.getTime() + 5 * 60 * 1000); // 5 minutes from now

        otsevents.forEach((event: OtsAircraft) => {
            let advTime = event.AdviseTimeUTC ? new Date(event.AdviseTimeUTC) : null;
            let etrTime = event.EstimatedReturnToServiceTimeUTC ? new Date(event.EstimatedReturnToServiceTimeUTC) : null;
            // Check if event is overdue or within 5 minutes
            if (advTime && currentTime >= advTime || etrTime && currentTime >= etrTime) {
                let alertTimeDiff = calculateAlertDiffTime(currentTime, advTime ? advTime : etrTime);
                let eventWithAlertTime = {...event, alertTimeDiffInMins: alertTimeDiff};
                overdueList.push(eventWithAlertTime);
            } else if (advTime && currentTime < advTime && advTime <= fiveMinsFromNow 
                || etrTime && currentTime < etrTime && etrTime <= fiveMinsFromNow) 
            {
                let alertTimeDiff = calculateAlertDiffTime(fiveMinsFromNow, advTime ? advTime : etrTime);
                let eventWithAlertTime = {...event, alertTimeDiffInMins: alertTimeDiff};
                fiveMinsList.push(eventWithAlertTime);
            }
        });

        // Sort the lists in ascending order based on alertTimeDiff
        overdueList.sort((a, b) => a.alertTimeDiffInMins - b.alertTimeDiffInMins);
        fiveMinsList.sort((a, b) => a.alertTimeDiffInMins - b.alertTimeDiffInMins);

        return { overdueList, fiveMinsList };
    };

    const calculateAlertDiffTime = (time: Date, eventTime: Date | null) => {
        if (eventTime) {
            // Calculate the duration since the alert was created in whole minutes
            return Math.floor((time.getTime() - eventTime.getTime()) / 60000);
        }
    }

    const handleAlertsClick = () => {
        openModal();
    };

    const handleAlertNotificationCLicked = (event: any) => {
            alertNotificationCLicked(event);
            closeModal();
    };

    return (
        <React.Fragment>
            <span ref={alertsModalRef}>
            <div className="alert-icon" onClick={handleAlertsClick} role="button" tabIndex={0} 
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAlertsClick();
                }
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="35" height="55" viewBox={ alertCount ? "0 0 32 28" : "-3 0 32 35" } fill="none" className="alert-box">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.335 0.0625C10.8413 0.0625 11.2597 0.438705 11.3259 0.926806L11.335 1.0625L11.3358 3.11451C15.3376 3.53851 18.0017 6.51884 18.0017 10.7292C18.0423 12.4849 18.3535 14.5968 18.9666 15.8129L19.1026 16.0593L20.5026 18.1778C20.9161 18.8035 20.5181 19.6256 19.8055 19.7202L19.6683 19.7292H13.3347L13.335 21.0625C13.335 22.6815 11.954 24.0625 10.335 24.0625C8.78351 24.0625 7.4505 22.7941 7.34211 21.2635L7.335 21.0625L7.33467 19.7292H1.00167C0.251681 19.7292 -0.214755 18.9439 0.0992417 18.2972L0.167383 18.1778L1.54699 16.0915C2.32085 14.8175 2.66834 12.7397 2.66834 10.7292C2.79814 6.68547 5.43274 3.57392 9.33512 3.1197L9.335 1.0625C9.335 0.510215 9.78272 0.0625 10.335 0.0625ZM11.3347 19.7292H9.33467L9.335 21.0625C9.335 21.5769 9.82062 22.0625 10.335 22.0625C10.8065 22.0625 11.2539 21.6544 11.3252 21.1902L11.335 21.0625L11.3347 19.7292ZM10.335 5.0625C6.98869 5.0625 4.79993 7.15284 4.67407 10.4282L4.66834 10.7292L4.66295 11.1991C4.61597 13.2359 4.25771 15.2632 3.4343 16.813L3.23596 17.162L2.86034 17.7292H17.8083L17.4137 17.1298C16.5309 15.6766 16.1146 13.6804 16.022 11.6374L16.007 11.1991L16.0017 10.7292C16.0017 7.28145 13.7827 5.0625 10.335 5.0625Z"
                        fill="#84868B"
                    />
                    {alertCount > 0 && (
                        <>
                            <circle className="alert-red-circle" cx="18" cy="2" r="10" fill="#DB0B00" />
                            <text className="alert-count" x="18" y="5.5" textAnchor="middle" fill="white">
                                {alertCount}
                            </text>
                        </>
                    )}
                </svg>
            </div>
            <AlertsModal isOpen={isModalOpen} onClose={closeModal} overdueEventsList={liveEventsOverdueList} fiveMinsList={liveEventsFiveMinsList} alertNotificationCLicked={handleAlertNotificationCLicked}/>
            </span>
        </React.Fragment>
    );
};

export default Alerts;